import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import SearchIcon from "@mui/icons-material/Search";
import { Box, IconButton } from "@mui/material";
import { t } from "i18next";
import toast from "react-hot-toast";
import { SnackbarError } from "../../components/base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";
import { ClientPassportButton } from "../../components/buttons/ClientPassportButton";
import { ClientConfirmDiscoveryDialog } from "../../components/dialog/ClientDiscoveryDialog/ClientConfirmDiscoveryDialog";
import { ClientDiscoveryDialog } from "../../components/dialog/ClientDiscoveryDialog/ClientDiscoveryDialog";
import { SearchOtherClientDialog } from "../../components/dialog/ClientDiscoveryDialog/SearchOtherClientDialog";
import { DATE_RANGE_FIELD_TYPE, SELECT_FIELD_TYPE, TEXT_FIELD_TYPE } from "../../helpers/constants";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import DateService from "../../services/dateService";
import {
    useAddSellerToLeadMutation,
    useCorrectLeadPersonMutation,
    useEditLeadMutation,
    useLeadFailureReasons,
    useLeadsSourceChannels,
    useLeadsStatuses,
} from "../../services/leadsService";
import { useInvestments } from "../../services/productsService";
import { useGetSellers } from "../../services/usersApi";
import { CustomerAddPersonDialog } from "../CustomerPage/CustomerAddPersonDialog";
import { ContactsDialog } from "../PersonRepositoryPage/ContactsDialog";

export const LEADS_TABLE_NAME = "leads_table";

export const useLeadsTableFieldsConfig = () => {
    const { openDialog: openContactsDialog, closeDialog: closeContactsDialog } = useDialogBetter();
    const { openDialog: openClientDiscoveryDialog } = useDialogBetter();
    const { openDialog: openCustomerAddPersonDialog, closeDialog: closeCustomerAddPersonDialog } =
        useDialogBetter();
    const {
        openDialog: openConfirmClientDiscoveryDialog,
        closeDialog: closeConfirmClientDiscoveryDialog,
    } = useDialogBetter();
    const { openDialog: openSearchOtherClientDialog, closeDialog: closeSearchOtherClientDialog } =
        useDialogBetter();
    const { mutate: correctLeadPerson } = useCorrectLeadPersonMutation();
    const { mutate: addSellerToLead } = useAddSellerToLeadMutation();

    const { data: statuses } = useLeadsStatuses({
        select: (data) =>
            data.results.map((status) => ({
                value: status.status_name_eng,
                label: t(`lead.status.${status.status_name_eng}`),
            })),
    });

    const { data: investments } = useInvestments({
        select: (data) =>
            data.results.map((investment) => ({
                value: investment.id,
                label: investment.investment_name_sales,
            })),
    });

    const { data: sources } = useLeadsSourceChannels({
        select: (data) =>
            data.results.map((channel) => ({
                value: channel.channel_name_eng,
                label: t(`lead.sources.${channel.channel_name_eng}`),
            })),
    });

    const { data: sellers } = useGetSellers({
        select: (data) =>
            data.map((seller) => ({
                value: seller.id,
                label: seller.full_name,
            })),
    });

    const { data: reasons } = useLeadFailureReasons({
        select: (data) =>
            data.results.map((reason) => ({
                value: reason.reason_name_eng,
                label: t(`lead.reasons.${reason.reason_name_eng}`),
            })),
    });

    const { mutate: editLead } = useEditLeadMutation({
        onSuccess: () => {
            toast.custom(
                () => <SnackbarSuccess message={t("leads.table.leadAssignedSuccessfully")} />,
                {
                    position: "bottom-right",
                },
            );
        },
        onError: (error) => {
            toast.custom(() => <SnackbarError message={t("leads.table.errorAssigningLead")} />, {
                position: "bottom-right",
            });
        },
    });

    return [
        {
            name: "fname",
            getValueCallback: (rowData) => rowData.person.person_name,
            label: t("leads.table.firstName"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
        },
        {
            name: "lname",
            getValueCallback: (rowData) => rowData.person.person_surname,
            label: t("leads.table.lastName"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: true,
        },
        {
            name: "source",
            getValueCallback: (rowData) => t(`lead.sources.${rowData.lead.lead_source}`),
            label: t("leads.table.contactSource"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                options: sources,
                optionKey: "value",
                optionLabel: "label",
            }),
            sortable: true,
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: sources,
            },
        },       
        {
            name: "created",
            getValueCallback: (rowData) =>
                DateService.convertDatetimeFromBackendToFormatYYYYMMDD(rowData.lead.created),
            label: t("leads.table.entryDate"),
            filterType: DATE_RANGE_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                style: { minWidth: "100px" },
            }),
            sortable: true,
        },
        {
            name: "email",
            getValueCallback: (rowData) => rowData.person.email.email_address,
            label: t("leads.table.email"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
        },
        {
            name: "phone",
            getValueCallback: (rowData) => rowData.person.phone.phone_number,
            label: t("leads.table.phoneNumber"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
        },
        {
            name: "status",
            getValueCallback: (rowData) => t(`lead.status.${rowData.lead.lead_status}`),
            label: t("leads.table.status"),
            filterType: SELECT_FIELD_TYPE,
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: statuses,
            },
            getFilterFieldConfigCallback: (filteringData) => ({
                options: statuses,
                optionKey: "value",
                optionLabel: "label",
            }),
            sortable: true,
        },
        {
            name: "product_number",
            getValueCallback: (rowData) => {
                return rowData.lead.products.map((product) => product.product_number).join(", ");
            },
            label: t("leads.table.product"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
        },
        {
            name: "investment",
            getValueCallback: (rowData) => rowData.lead?.investment?.investment_name_sales || "-",
            label: t("leads.table.investment"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                optionKey: "value",
                optionLabel: "label",
                options: investments,
                multiple: false,
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: investments,
            },
            sortable: false,
            minWidth: 120,
            width: 120,
        },
        {
            name: "note_source",
            getValueCallback: (rowData) => rowData.lead.lead_note_from_source,
            label: t("leads.table.authorComment"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
        },
        {
            name: "seller",
            getValueCallback: (rowData) => rowData.lead.lead_seller.full_name,
            label: t("leads.table.caretaker"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                optionKey: "value",
                optionLabel: "label",
                options: sellers,
                multiple: false,
            }),
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: sellers,
            },
            sortable: true,
        },
        {
            name: "note_import",
            getValueCallback: (rowData) => rowData.lead.lead_note_from_import,
            label: t("leads.table.sourceFileNote"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
        },
        {
            name: "reason",
            getValueCallback: (rowData) => t(`lead.reasons.${rowData.lead.lead_status_reason}`),
            label: t("leads.table.rejectionReason"),
            filterType: SELECT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => ({
                options: reasons,
                optionKey: "value",
                optionLabel: "label",
            }),
            sortable: true,
            fieldConfig: {
                optionKey: "value",
                optionLabel: "label",
                options: reasons,
            },
        },
        {
            name: "lead_status_note",
            getValueCallback: (rowData) => rowData.lead.lead_status_note,
            label: t("leads.table.statusNote"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (_) => undefined,
        },
        {
            name: "note_seller",
            getValueCallback: (rowData) => rowData.lead.lead_note_from_sales,
            label: t("leads.table.sellerComment"),
            filterType: TEXT_FIELD_TYPE,
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
        },
        {
            name: "contact",
            getValueCallback: (rowData) => (
                <IconButton
                    color="primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        openContactsDialog({
                            Component: ContactsDialog,
                            componentProps: {
                                personId: rowData.perslead_person_id,
                            },
                        });
                    }}
                >
                    <LocalPhoneIcon />
                </IconButton>
            ),
            label: t("leads.table.contact"),
            // @ts-ignore
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
            excludedFromExport: true,
            allowChangeVisibility: false,
        },
        {
            name: "discovery",
            getValueCallback: (rowData) => {
                // If there's an existing client, show the passport icon
                if (rowData.existing_customer_id != null) {
                    return (
                        <ClientPassportButton
                            clientId={rowData.existing_customer_id}
                            clientType="customer"
                        />
                    );
                }

                // If there's a discovered client, show the discovery dialog
                if (rowData.person.person_probable_customer_id) {
                    return (
                        <IconButton
                            color="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();

                                // Fetch client details to display in the discovery dialog
                                openClientDiscoveryDialog({
                                    Component: ClientDiscoveryDialog,
                                    componentProps: {
                                        title: t("leads.table.clientRecognition"),
                                        customerId: rowData.person.person_probable_customer_id,
                                        leadId: rowData.lead.id,
                                        person: rowData.person,
                                        defaultData: {
                                            personName: rowData.person.person_name,
                                            personSurname: rowData.person.person_surname,
                                            personEmail: rowData.person.email.email_address,
                                            personPhone: rowData.person.phone.phone_number,
                                        },
                                    },
                                });
                            }}
                        >
                            <PersonSearchIcon />
                        </IconButton>
                    );
                }

                // If no client exists or discovered, show both add and search buttons
                return (
                    <Box display="flex" gap={0.5}>
                        <IconButton
                            color="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                openCustomerAddPersonDialog({
                                    Component: CustomerAddPersonDialog,
                                    componentProps: {
                                        defaultData: {
                                            personName: rowData.person.person_name,
                                            personSurname: rowData.person.person_surname,
                                            personEmail: rowData.person.email.email_address,
                                            personPhone: rowData.person.phone.phone_number,
                                        },
                                        actualClauses: rowData.person.actual_clauses,
                                        personId: rowData.person.id,
                                        onSubmitSuccess: (data) => {
                                            if (data.person_id) {
                                                setTimeout(() => {
                                                    openConfirmClientDiscoveryDialog({
                                                        Component: ClientConfirmDiscoveryDialog,
                                                        componentProps: {
                                                            title: t("leads.table.assignCaretaker"),
                                                            onConfirm: (sellerData) => {
                                                                addSellerToLead({
                                                                    leadId: rowData.lead.id,
                                                                    sellerId: sellerData.sellerId,
                                                                });
                                                                closeConfirmClientDiscoveryDialog();
                                                                closeCustomerAddPersonDialog();
                                                            },
                                                            personId: data.person_id,
                                                            customerId: data.id,
                                                        },
                                                    });
                                                }, 100);
                                            }
                                        },
                                    },
                                });
                            }}
                        >
                            <PersonAddAlt1Icon />
                        </IconButton>
                        <IconButton
                            color="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                openSearchOtherClientDialog({
                                    Component: SearchOtherClientDialog,
                                    componentProps: {
                                        title: t("clients.discovery.searchClient"),
                                        defaultValues: {
                                            // don't include person_name by default
                                            // name: rowData.person.person_name,
                                            surname: rowData.person.person_surname,
                                            email: rowData.person.email.email_address,
                                            phone: rowData.person.phone.phone_number,
                                        },
                                        onCustomerSelected: (customer) => {
                                            closeSearchOtherClientDialog();

                                            // Open confirmation dialog to assign seller
                                            setTimeout(() => {
                                                openConfirmClientDiscoveryDialog({
                                                    Component: ClientConfirmDiscoveryDialog,
                                                    componentProps: {
                                                        title: t(
                                                            "clients.discovery.assignCaretaker",
                                                        ),
                                                        onConfirm: (sellerData) => {
                                                            correctLeadPerson({
                                                                person: customer.person?.id,
                                                                lead: rowData.lead.id,
                                                                seller: sellerData.sellerId,
                                                            });
                                                            closeConfirmClientDiscoveryDialog();
                                                        },
                                                        personId: customer.person?.id,
                                                        customerId: customer.id,
                                                    },
                                                });
                                            }, 100);
                                        },
                                    },
                                });
                            }}
                        >
                            <SearchIcon />
                        </IconButton>
                    </Box>
                );
            },
            label: t("leads.table.client"),
            // @ts-ignore
            getFilterFieldConfigCallback: (filteringData) => undefined,
            sortable: false,
            excludedFromExport: true,
            allowChangeVisibility: false,
        },
    ];
};
